import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { useAutoRefillSettings } from '@/hooks/useBilling';
import { toast } from 'sonner';
import { usePricingConfig, calculatePriceFromTiers } from '@/hooks/usePricingConfig';

export function AutoRefillTab({ hasPaymentMethod }: { hasPaymentMethod?: boolean }) {
  const { data: pricingData } = usePricingConfig();
  const { data: settings, isLoading, updateSettings } = useAutoRefillSettings();
  
  // Local state for all settings
  const [localEnabled, setLocalEnabled] = useState(false);
  const [localThreshold, setLocalThreshold] = useState(50);
  const [localRefillAmount, setLocalRefillAmount] = useState(150);
  
  // Track last saved values to prevent unnecessary updates
  const lastSavedEnabledRef = useRef<boolean>(false);
  const lastSavedThresholdRef = useRef<number>(50);
  const lastSavedRefillAmountRef = useRef<number>(150);
  
  // Get pricing tiers from database or fallback
  const tiers = pricingData?.tiers || [];
  const calculatePrice = (credits: number) => calculatePriceFromTiers(credits, tiers);

  // Initialize local state when settings first load or change
  useEffect(() => {
    if (settings) {
      const enabled = settings.enabled || false;
      const threshold = settings.threshold_credits || 50;
      const refillAmount = settings.refill_amount_credits || 150;
      
      // Only update if values have actually changed from what we have
      if (enabled !== lastSavedEnabledRef.current ||
          threshold !== lastSavedThresholdRef.current ||
          refillAmount !== lastSavedRefillAmountRef.current) {
        
        setLocalEnabled(enabled);
        setLocalThreshold(threshold);
        setLocalRefillAmount(refillAmount);
        
        lastSavedEnabledRef.current = enabled;
        lastSavedThresholdRef.current = threshold;
        lastSavedRefillAmountRef.current = refillAmount;
      }
    }
  }, [settings?.enabled, settings?.threshold_credits, settings?.refill_amount_credits]);

  const handleToggle = (enabled: boolean) => {
    if (!hasPaymentMethod && enabled) {
      toast.error('Please add a payment method first');
      return;
    }
    
    // Update local state immediately
    setLocalEnabled(enabled);
    lastSavedEnabledRef.current = enabled;
    
    // Update backend
    updateSettings.mutate({ enabled }, {
      onSuccess: () => {
        toast.success('Auto-refill settings updated');
      },
      onError: () => {
        // Revert local state on error
        setLocalEnabled(!enabled);
        lastSavedEnabledRef.current = !enabled;
      }
    });
  };

  const handleThresholdChange = (value: number[]) => {
    setLocalThreshold(value[0]);
  };
  
  const handleThresholdCommit = (value: number[]) => {
    const newValue = value[0];
    if (newValue !== lastSavedThresholdRef.current) {
      lastSavedThresholdRef.current = newValue;
      updateSettings.mutate({ threshold_credits: newValue }, {
        onSuccess: () => {
          toast.success('Auto-refill threshold updated');
        }
      });
    }
  };

  const handleRefillAmountChange = (value: number[]) => {
    setLocalRefillAmount(value[0]);
  };
  
  const handleRefillAmountCommit = (value: number[]) => {
    const newValue = value[0];
    if (newValue !== lastSavedRefillAmountRef.current) {
      lastSavedRefillAmountRef.current = newValue;
      updateSettings.mutate({ refill_amount_credits: newValue }, {
        onSuccess: () => {
          toast.success('Auto-refill amount updated');
        }
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Auto-refill Settings</CardTitle>
          <CardDescription>Loading settings...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const refillCost = calculatePrice(localRefillAmount);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Auto-refill Settings</CardTitle>
          <CardDescription>
            Automatically purchase credits when your balance runs low
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!hasPaymentMethod && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                You need to add a payment method before enabling auto-refill.
              </AlertDescription>
            </Alert>
          )}

          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-refill-toggle">Enable Auto-refill</Label>
              <p className="text-sm text-muted-foreground">
                Automatically purchase credits when balance is low
              </p>
            </div>
            <Switch
              id="auto-refill-toggle"
              checked={localEnabled}
              onCheckedChange={handleToggle}
              disabled={!hasPaymentMethod}
            />
          </div>

          {localEnabled && (
            <>
              {/* Threshold Setting */}
              <div className="space-y-4">
                <div>
                  <Label>Refill Threshold</Label>
                  <p className="text-sm text-muted-foreground">
                    Purchase credits when balance drops below this amount
                  </p>
                </div>
                <Slider
                  value={[localThreshold]}
                  onValueChange={handleThresholdChange}
                  onValueCommit={handleThresholdCommit}
                  min={10}
                  max={200}
                  step={10}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-sm">
                  <span>10 credits</span>
                  <span className="font-medium">{localThreshold} credits</span>
                  <span>200 credits</span>
                </div>
              </div>

              {/* Refill Amount Setting */}
              <div className="space-y-4">
                <div>
                  <Label>Refill Amount</Label>
                  <p className="text-sm text-muted-foreground">
                    Number of credits to purchase each time
                  </p>
                </div>
                <Slider
                  value={[localRefillAmount]}
                  onValueChange={handleRefillAmountChange}
                  onValueCommit={handleRefillAmountCommit}
                  min={35}
                  max={1250}
                  step={5}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-sm">
                  <span>35</span>
                  <span className="font-medium">{localRefillAmount} credits</span>
                  <span>1250</span>
                </div>
              </div>

              {/* Summary */}
              <div className="rounded-lg bg-muted p-4 space-y-2">
                <div className="flex items-center gap-2 font-medium">
                  <RefreshCw className="h-4 w-4" />
                  Auto-refill Summary
                </div>
                <p className="text-sm text-muted-foreground">
                  When your balance drops below {localThreshold} credits, 
                  we'll automatically add {localRefillAmount} credits 
                  (${refillCost.toFixed(2)}) to your account.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>How Auto-refill Works</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                <RefreshCw className="h-3 w-3 text-primary" />
              </div>
              <div>
                <div className="font-medium">Automatic Monitoring</div>
                <div className="text-sm text-muted-foreground">
                  We check your credit balance after each call
                </div>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                <RefreshCw className="h-3 w-3 text-primary" />
              </div>
              <div>
                <div className="font-medium">Instant Purchase</div>
                <div className="text-sm text-muted-foreground">
                  Credits are added immediately when threshold is reached
                </div>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                <RefreshCw className="h-3 w-3 text-primary" />
              </div>
              <div>
                <div className="font-medium">Email Notifications</div>
                <div className="text-sm text-muted-foreground">
                  You'll receive a receipt for each auto-refill purchase
                </div>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <div className="rounded-full bg-primary/10 p-1 mt-0.5">
                <RefreshCw className="h-3 w-3 text-primary" />
              </div>
              <div>
                <div className="font-medium">Daily Limit</div>
                <div className="text-sm text-muted-foreground">
                  Auto-refill triggers at most once per 24 hours to prevent overcharging
                </div>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
} 